import { useSuspenseQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType, type ResourceOutput } from '@cotera/api';

export type DocumentApiData = {
  id: string;
  orgId: string;
  ownerId: string;
  editorState: unknown;
  locked: boolean;
  title: string | null;
  description: string | null;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
};

export function toDocumentApiData(raw: {
  id: string;
  orgId: string;
  ownerId: string;
  editorState?: unknown;
  locked?: boolean;
  title: string | null;
  description: string | null;
  status: 'active' | 'archived';
  createdAt: Date | string;
  updatedAt: Date | string;
}): DocumentApiData {
  return {
    ...raw,
    editorState: raw.editorState ?? { type: 'doc', content: [] },
    locked: raw.locked ?? false,
    createdAt:
      raw.createdAt instanceof Date ? raw.createdAt : new Date(raw.createdAt),
    updatedAt:
      raw.updatedAt instanceof Date ? raw.updatedAt : new Date(raw.updatedAt),
  };
}

export type DocumentResourceApiData = {
  document: DocumentApiData;
  meta: {
    folderId: string | null;
    folderScope?: 'user' | 'org' | 'external' | null;
    permission: 'view' | 'edit' | 'admin';
  } | null;
};

export function toDocumentResourceApiData(
  output: ResourceOutput
): DocumentResourceApiData | null {
  if (output.type !== 'document') {
    return null;
  }
  const raw = output.document;
  if (
    raw === undefined ||
    raw === null ||
    typeof raw !== 'object' ||
    typeof raw.id !== 'string'
  ) {
    return null;
  }
  return {
    document: toDocumentApiData(raw),
    meta: output.meta
      ? {
          folderId: output.meta.folderId,
          folderScope: output.meta.folderScope,
          permission:
            output.meta.permission === 'pending'
              ? 'view'
              : output.meta.permission,
        }
      : null,
  };
}

/**
 * Suspenseful hook to fetch document data from the API.
 * Returns null when the document does not exist (404).
 */
export const useDocumentQuery = (
  documentId: string
): DocumentResourceApiData | null => {
  const org = useWhoami((s) => s.org);
  const client = useTenantedClient();

  const { data } = useSuspenseQuery({
    queryKey: queryKeys.resource.get({
      orgId: org.id,
      type: ResourceType.Document,
      id: documentId,
    }),
    queryFn: () =>
      client.resource.get({
        type: ResourceType.Document,
        id: documentId,
      }),
  });

  if (data.isErr()) {
    const appErr = data.error.t === 'application' ? data.error.err : null;
    if (
      appErr !== null &&
      (appErr.errorType === 'NotFound' || appErr.errorType === 'Forbidden')
    ) {
      return null;
    }
    const message = appErr?.message ?? 'Failed to load document';
    throw new Error(message);
  }
  if (data.value.type !== 'document') {
    return null;
  }
  return toDocumentResourceApiData(data.value);
};
